import "./index.css";
import { BellIcon } from "lucide-react";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { NotificationsRepository } from "@/api/notifications/repository";
import { NotificationsService } from "@/api/notifications/service";
import type { IStreamNotificationsResponse } from "@/api/notifications/types";
import { HeaderMenu } from "@/components/Notifications/components/HeaderMenu";
import { useBreakpoints } from "@/hooks/useBreakpoints";
import { useNotificationsStore } from "@/store/notifications/slice";
import { Badge, Drawer, IconButton, Popover, Tooltip } from "@/ui-kit";
import { Notifications } from "../../index";

// Paths where notification stream should not be initialized
const blacklistUrl: string[] = [
  "select-tenant",
  "auth",
  "login",
  "logout",
  "register",
];

export const NotificationButton = () => {
  const { t } = useTranslation();
  const count = useNotificationsStore((s) => s.count);
  const open = useNotificationsStore((s) => s.open);
  const streamRef = useRef<IStreamNotificationsResponse | null>(null);
  const { pathname } = useLocation();
  const isBlacklisted = blacklistUrl.some((url) => {
    return pathname.includes(url);
  });
  const { isMobile } = useBreakpoints();

  const onOpenChange = useCallback(() => {
    useNotificationsStore
      .getState()
      .setOpen(!useNotificationsStore.getState().open);
  }, []);

  useEffect(() => {
    const repository = new NotificationsRepository(new NotificationsService(), {
      abort: true,
    });

    const connectStream = () => {
      streamRef.current = repository.stream({
        onUnreadCount: (unreadCount) => {
          useNotificationsStore.getState().setCount(unreadCount);
        },
        onClose: (error) => {
          if (!error) {
            connectStream();
          }
        },
      });
    };

    connectStream();

    return () => {
      streamRef.current?.close();
    };
  }, []);

  if (isBlacklisted) {
    return null;
  }

  if (isMobile) {
    return (
      <>
        <IconButton
          variant="ghost"
          color="neutral"
          size="md"
          content={
            <Badge
              className="notifications-badge"
              size="md"
              hide={!count}
              anchor={<BellIcon />}
              content={count}
            />
          }
          onClick={onOpenChange}
          aria-label={t("label.notifications")}
        />
        {open && (
          <Drawer
            open={true}
            onOpenChange={onOpenChange}
            className="notifications-drawer"
          >
            <Drawer.Header title={t("label.notifications")}>
              <HeaderMenu />
            </Drawer.Header>
            <Drawer.Body>
              <Notifications />
            </Drawer.Body>
          </Drawer>
        )}
      </>
    );
  }

  return (
    <Tooltip content={t("label.notifications")} placement="bottom-end">
      <Popover
        open={open}
        onOpenChange={onOpenChange}
        content={<Notifications />}
        contentClassName="notifications-dropdown-content"
        placement="bottom-end"
        maxWidth={420}
      >
        <IconButton
          variant="ghost"
          color="neutral"
          size="md"
          content={
            <Badge
              className="notifications-badge"
              size="md"
              hide={!count}
              anchor={<BellIcon />}
              content={count}
            />
          }
          aria-label={t("label.notifications")}
        />
      </Popover>
    </Tooltip>
  );
};
